import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";

import { CORE_SPELLS } from "../../catalogs/spellsCatalog";

const catalogById = new Map(CORE_SPELLS.map((spell) => [spell.id, spell]));

const resolveSpell = (spell, t, language) => {
  const catalogSpell = catalogById.get(spell.catalogId);
  if (!catalogSpell) {
    return {
      ...spell,
      name: spell.name || t("spells.unknown"),
      spellLevel: spell.spellLevel || t("spells.unknownCircle"),
      castTime: spell.castTime || t("spells.source"),
      range: spell.range || t("spells.source"),
      notes: spell.notes || "",
    };
  }

  const lowestLevel = Math.min(...Object.values(catalogSpell.classLevels));
  const usesEnglishCatalog = language === "en";
  return {
    ...spell,
    name: usesEnglishCatalog
      ? catalogSpell.name.en || catalogSpell.name.ru
      : catalogSpell.name.ru || catalogSpell.name.en,
    searchableName: `${catalogSpell.name.ru} ${catalogSpell.name.en}`,
    spellLevel:
      lowestLevel === 0
        ? t("spells.cantrip")
        : `${t("spells.circle")} ${lowestLevel}`,
    castTime: t("spells.source"),
    range: t("spells.source"),
    notes: Object.entries(catalogSpell.classLevels)
      .map(([spellClass, level]) => `${spellClass} ${level}`)
      .join(" · "),
    referenceUrl: usesEnglishCatalog
      ? catalogSpell.referenceUrls.en || catalogSpell.referenceUrls.ru
      : catalogSpell.referenceUrls.ru || catalogSpell.referenceUrls.en,
  };
};

const SpellTable = ({ character, searchTerm, onRemove }) => {
  const { i18n, t } = useTranslation();
  const spells = useMemo(
    () =>
      (character.spellBook.spells || []).map((spell) =>
        resolveSpell(spell, t, i18n.resolvedLanguage || i18n.language)
      ),
    [character.spellBook.spells, i18n.language, i18n.resolvedLanguage, t]
  );
  const filteredSpells = filterSpells(spells, searchTerm);
  const spellsGroupedByLevel = groupSpellsByLevel(filteredSpells);
  const cantripLabel = t("spells.cantrip");

  if (filteredSpells.length === 0) {
    return (
      <div className="spell-container spell-empty-state">
        {spells.length === 0 ? t("spells.empty") : t("spells.noMatch")}
      </div>
    );
  }

  return (
    <div className="spell-container">
      {sortedSpellLevels(spellsGroupedByLevel, cantripLabel).map((level) => (
        <React.Fragment key={level}>
          <div className="spell-level-name"><span>{level}</span></div>
          <div className="spell-header">
            <span className="spell-name">{t("spells.name")}</span>
            <span className="spell-castTime">{t("spells.castTime")}</span>
            <span className="spell-range">{t("spells.range")}</span>
            <span className="spell-notes">{t("spells.notes")}</span>
            <span className="spell-actions" />
          </div>
          {spellsGroupedByLevel[level].map((spell, index) => (
            <div key={spell.id} className="spell">
              <span className="spell-name">{index + 1}. {spell.referenceUrl ? <a href={spell.referenceUrl} target="_blank" rel="noreferrer">{spell.name}</a> : spell.name}</span>
              <span className="spell-castTime">{spell.castTime}</span>
              <span className="spell-range">{spell.range}</span>
              <span className="spell-notes">{spell.notes}</span>
              <span className="spell-actions">
                <button
                  aria-label={t("spells.remove", { name: spell.name })}
                  className="spell-remove-button"
                  type="button"
                  onClick={() => onRemove(spell.id)}
                >
                  &times;
                </button>
              </span>
            </div>
          ))}
        </React.Fragment>
      ))}
    </div>
  );
};

export default SpellTable;

const filterSpells = (spellList, searchTerm) => {
  const normalizedSearch = searchTerm.toLowerCase();
  return spellList.filter((spell) =>
    [spell.name, spell.searchableName, spell.castTime, spell.range, spell.notes]
      .filter(Boolean)
      .some((value) => value.toLowerCase().includes(normalizedSearch))
  );
};

const groupSpellsByLevel = (spells) =>
  spells.reduce(
    (acc, spell) => ({
      ...acc,
      [spell.spellLevel]: [...(acc[spell.spellLevel] || []), spell],
    }),
    {}
  );

const sortedSpellLevels = (spellsGroupedByLevel, cantripLabel) =>
  Object.keys(spellsGroupedByLevel).sort((a, b) => {
    if (a === cantripLabel) return -1;
    if (b === cantripLabel) return 1;
    return parseInt(a.split(" ")[1], 10) - parseInt(b.split(" ")[1], 10);
  });
